import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  OptimizationBanner,
  RouteStatsBar,
  RouteMapPlaceholder,
  RouteStopList,
  StopDetailsModal,
} from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  RouteHeader,
  RouteStat,
  RouteStop,
  backendStopStatusToUi,
} from './driverRoutesTypes';
import { useDeliveriesStore } from '../../../store/deliveriesStore';
import { useRoutesStore } from '../../../store/routesStore';
import { DeliveryStop } from '../../../services/deliveriesApi';
import { formatOrderId, formatRouteId } from '../../../utils/formatId';
import styles from './DriverRoutes.module.css';

function adapt(s: DeliveryStop): RouteStop {
  return {
    id: String(s.id),
    index: s.sequence,
    name: s.customer_name || `Order ${s.order_id}`,
    address: s.customer_address || '—',
    contact: s.customer_phone || '—',
    orderId: formatOrderId(s.order_id),
    status: backendStopStatusToUi(s.status),
  };
}

function mapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

const DriverRoutes: React.FC = () => {
  const navigate = useNavigate();
  const stops = useDeliveriesStore((s) => s.stops);
  const loading = useDeliveriesStore((s) => s.loading);
  const error = useDeliveriesStore((s) => s.error);
  const fetchMyStops = useDeliveriesStore((s) => s.fetchMyStops);
  const myRoute = useRoutesStore((s) => s.myDriverRoute);
  const fetchMyRoute = useRoutesStore((s) => s.fetchMyDriverRoute);
  const updateRouteStatus = useRoutesStore((s) => s.updateRouteStatus);

  const [detailsStopId, setDetailsStopId] = useState<number | null>(null);
  const detailsStop = useMemo(
    () => (detailsStopId == null ? null : stops.find((s) => s.id === detailsStopId) ?? null),
    [stops, detailsStopId],
  );

  useEffect(() => {
    fetchMyRoute();
    fetchMyStops();
  }, [fetchMyRoute, fetchMyStops]);

  const adapted = useMemo<RouteStop[]>(() => stops.map(adapt), [stops]);

  const routeHeader: RouteHeader = {
    routeId: myRoute ? formatRouteId(myRoute.id) : '—',
    date: myRoute?.scheduled_date
      ? new Date(myRoute.scheduled_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
  };

  const completedCount = adapted.filter((s) => s.status === 'Delivered').length;
  const allDone = adapted.length > 0 && completedCount === adapted.length;
  const expectedCollections = stops.reduce((sum, s) => sum + s.order_total, 0);

  const routeStats: RouteStat[] = [
    { id: 'stops', label: 'Total Stops', value: String(adapted.length) },
    { id: 'orders', label: 'Total Orders', value: String(adapted.length) },
    { id: 'collections', label: 'Expected Collections', value: `$${expectedCollections.toFixed(2)}` },
    { id: 'progress', label: 'Progress', value: `${completedCount}/${adapted.length}` },
  ];

  const handleStopAction = (stop: RouteStop) => {
    const backend = stops.find((s) => String(s.id) === stop.id);
    if (stop.status === 'Pending' && backend?.customer_address) {
      window.open(mapsUrl(backend.customer_address), '_blank', 'noopener,noreferrer');
      return;
    }
    // Delivered (or any terminal state) → open read-only details modal with
    // POD photo/signature instead of routing to the active-delivery flow.
    if (stop.status === 'Delivered' && backend) {
      setDetailsStopId(backend.id);
      return;
    }
    // In Progress → go fulfill it.
    navigate('/active-deliveries');
  };

  const handleCompleteRoute = async () => {
    if (!allDone || !myRoute) return;
    try {
      await updateRouteStatus(myRoute.id, 'COMPLETED');
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to mark route complete');
      return;
    }
    navigate('/dashboard');
  };

  return (
    <div className={styles.container}>
      <OptimizationBanner />
      <div className={styles.card}>
        <div className={styles.headerRow}>
          <div>
            <h2 className={styles.routeId}>Route {routeHeader.routeId}</h2>
            <p className={styles.routeDate}>{routeHeader.date}</p>
          </div>
          <button
            className={styles.completeBtn}
            onClick={handleCompleteRoute}
            disabled={!allDone}
            title={allDone ? 'Mark route complete' : 'Finish all stops first'}
          >
            Complete Route
          </button>
        </div>

        <RouteStatsBar stats={routeStats} />
        <RouteMapPlaceholder />

        <AsyncBoundary
          loading={loading && stops.length === 0}
          error={error}
          empty={!loading && adapted.length === 0}
          emptyMessage="No stops assigned to your route yet"
          onRetry={() => fetchMyStops()}
        >
          <RouteStopList stops={adapted} onStopAction={handleStopAction} />
        </AsyncBoundary>
      </div>

      <StopDetailsModal
        open={!!detailsStop}
        stop={detailsStop}
        onClose={() => setDetailsStopId(null)}
      />
    </div>
  );
};

export const DriverRoutesPage: React.FC = () => <DriverRoutes />;

export { DriverRoutes };
export default DriverRoutes;
